/**
 * Utility for formatting JSON and nested objects with proper indentation
 */

/**
 * Format nested objects with proper indentation for all levels
 * @param obj - Object to format
 * @param depth - Current nesting depth (default: 0)
 * @param indentSize - Number of spaces per indent level (default: 2)
 * @returns Formatted string with proper indentation
 */
export function formatNestedObject(
	obj: any,
	depth = 0,
	indentSize = 2,
): string {
	const indent = ' '.repeat(depth * indentSize);
	const nextIndent = ' '.repeat((depth + 1) * indentSize);

	if (obj === null || obj === undefined) {
		return 'null';
	}

	if (typeof obj !== 'object') {
		// Check if it's a string that contains JSON
		if (typeof obj === 'string') {
			// Try to detect and parse JSON strings
			if (
				(obj.startsWith('[') && obj.endsWith(']')) ||
				(obj.startsWith('{') && obj.endsWith('}'))
			) {
				try {
					const parsed = JSON.parse(obj);
					return formatNestedObject(parsed, depth, indentSize);
				} catch {
					// If parsing fails, treat as regular string
					return JSON.stringify(obj);
				}
			}
		}
		return JSON.stringify(obj);
	}

	if (Array.isArray(obj)) {
		if (obj.length === 0) return '[]';
		const items = obj.map(
			(item) =>
				`${nextIndent}${formatNestedObject(item, depth + 1, indentSize)}`,
		);
		return `[\n${items.join(',\n')}\n${indent}]`;
	}

	const entries = Object.entries(obj);
	if (entries.length === 0) return '{}';

	const formattedEntries = entries.map(([key, value]) => {
		const formattedValue = formatNestedObject(value, depth + 1, indentSize);
		return `${nextIndent}"${key}": ${formattedValue}`;
	});

	return `{\n${formattedEntries.join(',\n')}\n${indent}}`;
}

/**
 * Enhanced JSON.stringify with better formatting for nested objects
 * @param obj - Object to stringify
 * @param indentSize - Number of spaces per indent level (default: 2)
 * @returns Formatted JSON string
 */
export function formatJSON(obj: any, indentSize = 2): string {
	return formatNestedObject(obj, 0, indentSize);
}
